<template>
  <modal :index="index" :prevent-esc="isSaving" :should-cancel-on="!isSaving" :state="state" class="fds sm"
         @toggle-modal="reset"
  >
    <h3 class="modal-title h3">Create new relationship</h3>
    <div class="modal-field">
      <label class="label-lg" for="relationship_label_title">{{ labelTitleDisplay }}</label>
      <input
        id="relationship_label_title"
        v-model="relationData.label_title"
        v-focus
        :disabled="isSaving"
        :placeholder="labelTitlePlaceholder"
        :readonly="isSaving"
        class="field_with_errors"
        type="text"
        @focus="currentlyActiveField = 'labelTitle'"
        @keyup.enter="create"
      />
      <template v-if="isInverseLabelable">
        <p class="text-grey">{{ relationTypeFriendlyName }}</p>
        <label class="label-lg" for="relationship_inverse_label_title">
          Role of Related Object Model(s)
        </label>
        <input
          id="relationship_inverse_label_title"
          v-model="relationData.inverse_label_title"
          :disabled="isSaving"
          :readonly="isSaving"
          class="field_with_errors"
          placeholder="Enter role of related object model(s)"
          type="text"
          @focus="currentlyActiveField = 'inverseLabelTitle'"
          @keyup.enter="create"
        />
      </template>
      <label class="label-lg" for="relationship_reference_name">
        Reference name
      </label>
      <input
        id="relationship_reference_name"
        v-model="relationData.reference_name"
        :disabled="isSaving"
        :placeholder="`Enter reference name`"
        :readonly="isSaving"
        class="field_with_errors"
        type="text"
        @focus="currentlyActiveField = 'referenceName'"
        @keyup.enter="create"
      />
    </div>

    <div v-if="isReferenceNamePresent && !isReferenceNameValid" class="error-text">Not a unique reference name.</div>
    <div class="cta">
      <button :disabled="isSaving" class="btn red outline block" type="button" @click="reset">Cancel</button>
      <button :disabled="!isValid || isSaving" class="btn green block" type="button" @click="create">
        Create
      </button>
    </div>
  </modal>
</template>

<script lang="ts">
import Vue from "vue"
import { RegexHelper } from "~/helpers/regexHelper"
import { generateReferenceName } from "~/helpers/referenceNameGenerator"
import { friendlyObjectRelationDisplay } from '~/enums/objectModelRelationType'
import { ObjectModelRelationCapability } from '~/models/objectModelRelations/objectModelRelationCapability'
import ObjectModelRelationCapabilityManager from '~/models/objectModelRelations/objectModelRelationCapabilityManager'

export default Vue.extend({
  props: {
    state: Object,
    relationType: {
      type: Number,
      required: true,
    },
    objectModel: {
      type: Object as () => { reference_name: string },
      required: true,
    },
    referenceNamesTaken: {
      type: Array as () => string[],
      required: true,
    },
  },
  data: function () {
    return {
      currentlyActiveField: "",
      labelTitle: '',
      relationData: {
        label_title: '',
        inverse_label_title: '',
        reference_name: '',
      },
      referenceNameEdited: false,
      isSaving: false,
    }
  },
  computed: {
    relationTypeFriendlyName(): string {
      return friendlyObjectRelationDisplay(this.relationType)
    },
    labelTitleIsValid() {
      return this.relationData.label_title.trim().length > 0
    },
    inverseLabelTitleIsValid() {
      return !this.isInverseLabelable || this.relationData.inverse_label_title.trim().length > 0
    },
    isReferenceNamePresent(): boolean {
      return this.relationData.reference_name.length > 0
    },
    isReferenceNameUnique(): boolean {
      return !this.referenceNamesTaken.includes(this.relationData.reference_name)
    },
    isReferenceNameValid(): boolean {
      return this.isReferenceNamePresent && this.relationData.reference_name.match(RegexHelper.urlSafe) && this.isReferenceNameUnique
    },
    isValid(): boolean {
      return this.labelTitleIsValid && this.inverseLabelTitleIsValid && this.isReferenceNameValid
    },
    isInverseLabelable(): boolean {
      return ObjectModelRelationCapabilityManager.can(this.relationType, ObjectModelRelationCapability.inverseLabelable)
    },
    labelTitleDisplay(): string {
      return this.isInverseLabelable ? 'Role of this Object Model' : 'Relationship name'
    },
    labelTitlePlaceholder(): string {
      return this.isInverseLabelable ? 'Enter role of this object model' : 'Enter relationship name'
    },
  },
  methods: {
    create() {
      if (this.isValid && !this.isSaving) {
        this.isSaving = true
        this.$emit("create", this.relationData)
      }
    },
    reset(event) {
      this.$emit("toggle-modal", { event: event, id: this.state.id, isVisible: false })
    },
    updateReferenceName() {
      if (this.referenceNameEdited) return

      if (this.isInverseLabelable) {
        this.relationData.reference_name = generateReferenceName(`${this.objectModel.reference_name}_${this.relationData.label_title}_${this.relationTypeFriendlyName}_${this.relationData.inverse_label_title}`)
      } else {
        this.relationData.reference_name = generateReferenceName(`${this.objectModel.reference_name}_${this.relationData.label_title}_${this.relationTypeFriendlyName}`)
      }
    },
  },
  watch: {
    'relationData.label_title'() {
      this.updateReferenceName()
    },
    'relationData.inverse_label_title'() {
      this.updateReferenceName()
    },
    referenceName() {
      if (this.currentlyActiveField === "referenceName") {
        this.referenceNameEdited = true
      }
    },
  },
})
</script>
